import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircle,
  CreditCard,
  Camera,
  Copy,
  MessageSquare,
  ShieldCheck,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import API from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { processRazorpayPayment } from '../lib/razorpay';
import { toast } from 'sonner';

const PaymentPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [txnId, setTxnId] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [paymentTab, setPaymentTab] = useState('razorpay'); // 'razorpay' or 'manual_upi'
  const [step, setStep] = useState(1);

  const orderType = searchParams.get('type') || 'order';
  const upiId = '88028343@ybl';

  useEffect(() => {
    const initiatePayment = async () => {
      try {
        const response = await axios.post(
          `${API}/payments/initiate`,
          { order_id: orderId, order_type: orderType },
          { withCredentials: true }
        );
        setPaymentData(response.data);
      } catch (error) {
        console.error('Payment init error:', error);
        toast.error('Failed to retrieve payment information');
      } finally {
        setLoading(false);
      }
    };
    initiatePayment();
  }, [orderId, orderType]);

  const handleRazorpayPay = async () => {
    setProcessing(true);
    await processRazorpayPayment({
      orderId,
      orderType,
      user,
      onSuccess: () => {
        setPaymentSuccess(true);
        toast.success('Payment verified successfully!');
        setTimeout(() => {
          if (orderType === 'booking') {
            navigate(`/tracking/${orderId}`);
          } else {
            navigate(`/orders/${orderId}?success=true`);
          }
        }, 1200);
      },
      onError: (errMsg) => {
        setProcessing(false);
        toast.error(errMsg || 'Payment failed. You can also pay via QR code below.');
      },
      onDismiss: () => {
        setProcessing(false);
      }
    });
  };

  const handleCopyUPI = () => {
    navigator.clipboard.writeText(upiId);
    toast.success('UPI ID copied to clipboard');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be under 5MB');
        return;
      }
      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmitManualDetails = async () => {
    if (!txnId && !screenshot) {
      toast.error('Please enter Transaction ID (UTR) or upload screenshot.');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      if (txnId) formData.append('txn_id', txnId);
      if (screenshot) formData.append('screenshot', screenshot);

      await axios.post(`${API}/payments/confirm/${paymentData.payment_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      setPaymentSuccess(true);
      toast.success('Payment details submitted successfully!');

      setTimeout(() => {
        if (orderType === 'booking') {
          navigate('/dashboard');
        } else {
          navigate(`/orders/${orderId}?success=true`);
        }
      }, 2000);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Verification failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F0]">
        <div className="w-10 h-10 rounded-full border-3 border-[#6E1B34] border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F0] p-4 text-[#2A1F16]">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-6 sm:p-10 text-center shadow-md border border-[#E7D9BC] max-w-sm w-full"
        >
          <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h1 className="font-serif-heading text-xl sm:text-2xl font-bold text-[#4E1225] mb-1.5">
            Payment Completed!
          </h1>
          <p className="text-xs text-[#83725B] mb-5">
            Your payment for {orderType === 'booking' ? 'puja booking' : 'order'} has been verified. Redirecting to your details...
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FBF4E7] text-[#6E1B34] rounded-full text-xs font-semibold border border-[#E7D9BC]">
            <span className="w-2 h-2 rounded-full bg-[#6E1B34] animate-ping"></span>
            <span>Opening Details...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex flex-col text-[#2A1F16]">
      <Navbar />

      <main className="flex-1 pt-20 sm:pt-24 pb-12 relative overflow-hidden flex items-center justify-center">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8D5A8]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="w-full max-w-md mx-auto px-4 relative z-10">
          
          <div className="text-center mb-5">
            <span className="inline-block px-2.5 py-0.5 bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] rounded-full text-[10px] font-bold uppercase tracking-wider mb-1.5">
              Secure Gateway
            </span>
            <h1 className="font-serif-heading text-2xl sm:text-3xl font-bold text-[#4E1225]">
              Complete <span className="italic text-[#6E1B34]">Payment</span>
            </h1>
          </div>

          <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-xs border border-[#E7D9BC] relative overflow-hidden space-y-4">
            
            {/* Amount Banner */}
            <div className="bg-[#FBF4E7] rounded-xl p-4 text-center border border-[#E7D9BC]">
              <span className="text-[#83725B] font-bold text-[10px] uppercase tracking-wider block">
                Total Payable Amount
              </span>
              <div className="font-price text-3xl sm:text-4xl font-bold text-[#6E1B34] mt-0.5">
                ₹{paymentData?.amount?.toLocaleString() || '0'}
              </div>
              <p className="text-[11px] text-[#83725B] mt-0.5">Order #{orderId.slice(0, 10)}</p>
            </div>

            {/* Selector Tabs */}
            <div className="grid grid-cols-2 p-1 bg-[#FBF4E7] rounded-xl border border-[#E7D9BC]">
              <button
                type="button"
                onClick={() => setPaymentTab('razorpay')}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${
                  paymentTab === 'razorpay'
                    ? 'bg-white text-[#4E1225] shadow-2xs'
                    : 'text-[#83725B] hover:text-[#4E1225]'
                }`}
              >
                Razorpay (Instant)
              </button>
              <button
                type="button"
                onClick={() => setPaymentTab('manual_upi')}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${
                  paymentTab === 'manual_upi'
                    ? 'bg-white text-[#4E1225] shadow-2xs'
                    : 'text-[#83725B] hover:text-[#4E1225]'
                }`}
              >
                Manual UPI / QR
              </button>
            </div>

            {paymentTab === 'razorpay' ? (
              <div className="space-y-4 pt-1">
                <div className="p-3 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl text-xs space-y-1">
                  <div className="flex items-center gap-1.5 text-[#2E7D45] font-bold">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Instant Verification & Invoice Email</span>
                  </div>
                  <p className="text-[#33691E] text-[11px] leading-relaxed">
                    Pay securely using UPI, Cards, or NetBanking. Your receipt is generated automatically.
                  </p>
                </div>

                <Button
                  onClick={handleRazorpayPay}
                  disabled={processing}
                  className="w-full rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white py-3 px-5 text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-4 h-4 text-[#E8D5A8]" />
                  <span>{processing ? 'Connecting Gateway...' : `Pay ₹${paymentData?.amount?.toLocaleString() || '0'} with Razorpay`}</span>
                </Button>

                <div className="text-center pt-1">
                  <a
                    href="https://wa.me/917011134934?text=I%20need%20help%20with%20my%20payment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#83725B] hover:text-[#2E7D45]"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Need help? WhatsApp: +91 7011134934</span>
                  </a>
                </div>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.div
                    key="upi-step-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4 pt-1"
                  >
                    <div className="text-center">
                      <p className="text-[11px] text-[#83725B] mb-2 font-semibold">
                        Scan QR Code in any UPI App
                      </p>
                      <div className="bg-white p-2.5 rounded-xl inline-block border border-[#E7D9BC]">
                        <img
                          src="/payment-qr.jpg"
                          alt="UPI QR"
                          className="w-40 h-40 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg';
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 bg-[#FBF4E7] border border-[#E7D9BC] py-2 px-3 rounded-lg text-xs">
                      <span className="font-mono font-bold text-[#2A1F16] flex-1">{upiId}</span>
                      <button
                        onClick={handleCopyUPI}
                        className="p-1 hover:bg-white rounded text-[#83725B]"
                        title="Copy"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <Button
                      onClick={() => setStep(2)}
                      className="w-full rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white py-2.5 text-xs font-bold"
                    >
                      <span>I Have Paid &rarr; Enter UTR</span>
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="upi-step-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3 pt-1 text-xs"
                  >
                    <div className="space-y-1">
                      <Label className="text-[11px] font-bold text-[#4E1225] uppercase">
                        UPI Transaction ID / UTR
                      </Label>
                      <Input
                        placeholder="12-digit UTR (e.g. 412345678901)"
                        value={txnId}
                        onChange={(e) => setTxnId(e.target.value)}
                        className="h-9 rounded-lg text-xs font-mono border-[#E7D9BC]"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-[11px] font-bold text-[#4E1225] uppercase">
                        Upload Screenshot
                      </Label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      {!previewUrl ? (
                        <div
                          onClick={() => fileInputRef.current.click()}
                          className="h-20 border border-dashed border-[#E7D9BC] rounded-lg bg-[#FAF7F0] flex flex-col items-center justify-center cursor-pointer hover:bg-white transition-all"
                        >
                          <Camera className="w-5 h-5 text-[#83725B] mb-1" />
                          <span className="text-[11px] text-[#83725B]">Select payment screenshot</span>
                        </div>
                      ) : (
                        <div className="relative rounded-lg overflow-hidden border border-[#E7D9BC] p-1.5 flex justify-center bg-[#FAF7F0]">
                          <img src={previewUrl} alt="Preview" className="h-24 object-contain rounded" />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                            className="absolute bottom-2 right-2 bg-white text-[10px] font-bold px-2 py-0.5 rounded border"
                          >
                            Change
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="pt-2 flex gap-2">
                      <Button
                        onClick={() => setStep(1)}
                        variant="outline"
                        className="flex-1 rounded-md border-[#E7D9BC] py-2 text-xs font-semibold"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleSubmitManualDetails}
                        disabled={processing || (!txnId && !screenshot)}
                        className="flex-[2] rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white py-2 text-xs font-bold"
                      >
                        {processing ? 'Submitting...' : 'Submit Verification'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
